import User from "@/Layouts/User";
import { useBuy } from "@/context/buy";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { encryptInteger } from "@/Utils/encryptInteger";
import Cookies from "js-cookie";
import Head from "next/head";
import { useRouter } from "next/router";
import ForgotPasswordModal from "@/Components/User/Modal/ForgotPasswordModal";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "@/firebase";
import CreateUserModal from "@/Components/User/Modal/CreateUserModal";
import { loadGoogleMapsScript } from "@/Utils/loadScript";
import MetaTags from "@/Components/SEO/MetaTags";
import { Schema } from "@/Components/SEO/Schema";
import withBaseUrl from "@/Utils/const";
import { motion } from "framer-motion";
import { useLoading } from "@/context/loading";

export default function index({ API_KEY }) {
  const router = useRouter();
  const { isLoading } = useLoading();
  const {
    directOpen,
    openSingleModal,
    setLoggedUserDetails,
    getBuyModalData,
    modelMap,
    setModalMap,
    setGoogleToken,
  } = useBuy();
  const [active, setActive] = useState(0);
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [createNewUserModalOpen, setCreateNewUserModalOpen] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    let loggedInUser = false;
    let encryptedData = localStorage.getItem("user_data");
    if (encryptedData) {
      loggedInUser = true;
      router.back();
    }

    if (API_KEY) {
      loadGoogleMapsScript(API_KEY)
        .then(() => {
          console.log("");
        })
        .catch((err) => {
          console.error("Google Maps script failed to load:", err);
          toast.error("Failed to load Google Maps.");
        });
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    }),

    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("action", "SignIn");
        formData.append("email", values.email);
        formData.append("password", values.password);

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        if (data.status) {
          const storingData = {
            profile: data.data.profile,
            web_token_expires_at: data.web_token_expires_at,
            web_token: data.web_token,
            name: data?.data?.name,
            state: data?.data?.state,
            user_type: data?.data?.user_type,
          };
          const encryptedData = encryptInteger(JSON.stringify(storingData));

          Cookies.set("user_data", JSON.stringify(storingData), {
            expires: 10,
            path: "/",
            secure: true,
            sameSite: "Lax",
          });

          localStorage.setItem("user_data", encryptedData);
          setLoggedUserDetails({
            status: true,
            data: storingData,
            remove: false,
          });
          if (getBuyModalData()) {
            openSingleModal("buy");
          }

          if (modelMap) {
            setModalMap(false);
          }
          let last_url = sessionStorage.getItem("last_url");
          
          // Check if last_url is a payment success page
          // Always redirect to dashboard instead - user has already seen payment success
          if (last_url && last_url.includes("/payment-success")) {
            router.push("/dashboard");
            return;
          }
          
          // Check if last_url is a ticket confirmation page
          if (last_url && last_url.includes("/tickets/")) {
            // Validate ticket exists before redirecting
            try {
              // Extract ticket ID from URL (handle both full URLs and paths)
              const urlPath = last_url.includes("http") 
                ? new URL(last_url).pathname 
                : last_url.split("?")[0].split("#")[0];
              const ticketId = urlPath.split("/tickets/")[1];
              
              if (ticketId) {
                const formData = new FormData();
                formData.append("action", "FetchTicketDetails_v3");
                formData.append("booking_id", ticketId);

                const ticketResponse = await fetch(
                  `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageTicket`,
                  {
                    method: "POST",
                    headers: { Authorization: `Bearer ${storingData.web_token}` },
                    body: formData,
                  }
                );

                const ticketData = await ticketResponse.json();
                
                // If ticket doesn't exist or is invalid, redirect to dashboard instead
                if (!ticketData.status) {
                  toast.error("Ticket not found. Redirecting to your dashboard.");
                  router.push("/dashboard");
                  return;
                }
              } else {
                // Invalid ticket URL format, redirect to dashboard
                router.push("/dashboard");
                return;
              }
            } catch (error) {
              console.error("Error validating ticket:", error);
              // If validation fails, redirect to dashboard
              router.push("/dashboard");
              return;
            }
          }
          
          router.push(last_url || "/dashboard");
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        console.error("API Error:", error.message);
      }
    },
  });

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const idToken = await user.getIdToken();

      const formData = new FormData();
      formData.append("action", "loginWithMedia");
      formData.append("firebase_token", idToken);
      formData.append("email", user.email);

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (data.status) {
        if (data.profile_status == 0) {
          setCreateNewUserModalOpen(true);
          setGoogleToken(data.token);
        } else {
          const storingData = {
            profile: data.data.profile,
            web_token_expires_at: data.web_token_expires_at,
            web_token: data.token,
            name: data?.data?.name,
            state: data?.data?.state,
            user_type: data?.data?.user_type,
          };
          const encryptedData = encryptInteger(JSON.stringify(storingData));

          Cookies.set("user_data", JSON.stringify(storingData), {
            expires: 10,
            path: "/",
            secure: true,
            sameSite: "Lax",
          });

          localStorage.setItem("user_data", encryptedData);
          setLoggedUserDetails({
            status: true,
            data: storingData,
            remove: false,
          });
          if (getBuyModalData()) {
            openSingleModal("buy");
          }

          if (modelMap) {
            setModalMap(false);
          }
          let last_url = sessionStorage.getItem("last_url");
          
          // Check if last_url is a payment success page
          // Always redirect to dashboard instead - user has already seen payment success
          if (last_url && last_url.includes("/payment-success")) {
            router.push("/dashboard");
            return;
          }
          
          // Check if last_url is a ticket confirmation page
          if (last_url && last_url.includes("/tickets/")) {
            // Validate ticket exists before redirecting
            try {
              // Extract ticket ID from URL (handle both full URLs and paths)
              const urlPath = last_url.includes("http") 
                ? new URL(last_url).pathname 
                : last_url.split("?")[0].split("#")[0];
              const ticketId = urlPath.split("/tickets/")[1];
              
              if (ticketId) {
                const formData = new FormData();
                formData.append("action", "FetchTicketDetails_v3");
                formData.append("booking_id", ticketId);

                const ticketResponse = await fetch(
                  `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageTicket`,
                  {
                    method: "POST",
                    headers: { Authorization: `Bearer ${storingData.web_token}` },
                    body: formData,
                  }
                );

                const ticketData = await ticketResponse.json();
                
                // If ticket doesn't exist or is invalid, redirect to dashboard instead
                if (!ticketData.status) {
                  toast.error("Ticket not found. Redirecting to your dashboard.");
                  router.push("/dashboard");
                  return;
                }
              } else {
                // Invalid ticket URL format, redirect to dashboard
                router.push("/dashboard");
                return;
              }
            } catch (error) {
              console.error("Error validating ticket:", error);
              // If validation fails, redirect to dashboard
              router.push("/dashboard");
              return;
            }
          }
          
          router.push(last_url || "/dashboard");
        }
      } else {
        toast.error(data?.message || "Error in Login Please Try Again");
        console.log("error", data.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something Went Wrong Please Try Again..");
      console.error("media error", error);
    }
  };

  const getBreadcrumbs = () => {
    return [
      {
        name: "Home",
        url: withBaseUrl("/"),
      },
      {
        name: "Account",
        url: withBaseUrl("/login"),
      },
    ];
  };

  const sendMagicLink = async () => {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`, {
        method: "POST",
        body: new URLSearchParams({
          action: "sentMagicLink",
          email: email,
        }),
      });
      const data = await response.json();
      if (data.status) {
        setEmail("")
        toast.success("Magic link sent successfully");
      } else {
        toast.error(data?.message || "Error in Login Please Try Again");
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <User>
      <MetaTags
        title="Login | Qoncert"
        description="Log in to your Qoncert account to manage your tickets, shows, and artist profile."
        ogImage="/assets/logo.webp"
        canonicalUrl="/login"
        noindex={true}
      />
      <Head>
        <title>Login | Qoncert</title>
        <Schema breadcrumbs={getBreadcrumbs()} />
      </Head>
      <div id="LoginModal" className="font-urbanist">
        <div className="layout_bg_image hidden z-[-9999] lg:block">
          <span className="bg_inner_shadow z-[-99"></span>
        </div>
        <div className="flex flex-col relative lg:items-center md:flex-row justify-start lg:justify-center overflow-y-auto h-full inset-0 px-7 md:px-0">
          <motion.div
            id="login-modal"
            initial={{ opacity: 0, y: 20 }}
            animate={!isLoading ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-[#17191d] relative z-[99] mb-10 h-fit w-full max-w-[620px] md:my-[20px] overflow-y-auto px-[20px] pb-[40px] rounded-[16px] lg:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-6 lg:mt-0 md:px-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="lg:mt-12 text-center lg:text-[24px] font-[500]"
              >
                <p>Log Into Your Account</p>
              </motion.div>
              <div className="relative mt-8 flex w-full items-center border-b-2 border-[#636568]">
                <div
                  onClick={() => setActive(0)}
                  className={`w-full text-lg cursor-pointer text-center font-[600] pb-1 ${active === 0 ? "text-white" : "text-[#636568]"
                    }`}
                >
                  Magic Link Login
                </div>
                <div
                  onClick={() => setActive(1)}
                  className={`w-full text-lg cursor-pointer text-center font-[600] pb-1 ${active === 1 ? "text-white" : "text-[#636568]"
                    }`}
                >
                  Password Login
                </div>

                <motion.div
                  layout
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="absolute bottom-[-2px] h-[2px] w-1/2 bg-[#FA2CB4]"
                  style={{
                    left: active === 0 ? "0%" : "50%",
                  }}
                />
              </div>
              {active === 0 && (
                <div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="mt-5 pl-2 lg:pl-0 lg:mt-7"
                  >
                    <div className="">
                      <div className="py-3 pb-8">
                        <p className="text-[17px] pr-14 text-[#cecece] ">
                          Log in with your email. We&apos;ll email you a link to
                          access your account.
                        </p>
                      </div>
                      <div className="form-item flex flex-col gap-2 lg:gap-5">
                        <label
                          className="text-[10px] lg:text-base text-[#718096]"
                          htmlFor="email"
                        >
                          Email address
                        </label>
                        <div className="relative">
                          <input
                            className="bg-[#E2E8F0] w-full text-[#2D3748] focus:outline-none text-[12px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                            placeholder="Enter your e-mail"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                    className="mt-8 lg:mt-12 modal-footer"
                  >
                    <button
                      onClick={() => {
                        sendMagicLink();
                      }}
                      className={`main_btn_bg lg:order-2 font-[600] text-[16px] w-full lg:text-lg py-[6px] lg:py-5 px-[62px] rounded-[10px] lg:leading-[10px] `}
                    >
                      {" "}
                      Send Magic Link
                    </button>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                    className="mt-24"
                  >
                    <p className="text-center cursor-pointer mt-2">Already have a password? <span className="text-[#FA2CB4] cursor-pointer" onClick={() => setActive(1)}>Login</span></p>
                    <p className="text-center cursor-pointer mt-2">Not Registered Yet? <span className="text-[#FA2CB4] cursor-pointer" onClick={() => router.push("/register")}>Create Account</span></p>
                  </motion.div>
                </div>
              )}
              {active === 1 && (
                <div>
                  <motion.form
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    action=""
                    className="mt-5 pl-2 lg:pl-0 lg:mt-7"
                  >
                    <div className="">
                      <div className="form-item flex flex-col gap-2 lg:gap-5">
                        <label
                          className="text-[10px] lg:text-base text-[#718096]"
                          htmlFor="email"
                        >
                          Email address
                        </label>
                        <div className="relative">
                          <input
                            className="bg-[#E2E8F0] w-full text-[#2D3748] focus:outline-none text-[12px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                            placeholder="Enter your e-mail"
                            type="email"
                            {...formik.getFieldProps("email")}
                            id="email"
                            required
                          />
                          {formik.touched.email && formik.errors.email ? (
                            <div className="text-red-500 text-sm">
                              {formik.errors.email}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="form-item mt-2 lg:mt-5 flex flex-col gap-2 lg:gap-3">
                        <label
                          className="text-[10px] lg:text-base text-[#718096]"
                          htmlFor="password"
                        >
                          Password
                        </label>
                        <div className="relative">
                          <input
                            className="bg-[#E2E8F0] w-full text-[#2D3748] focus:outline-none text-[12px] lg:text-base custom-password rounded-[5px] p-3 lg:p-3"
                            placeholder="Enter your password"
                            type="password"
                            id="password"
                            {...formik.getFieldProps("password")}
                            required
                          />
                          {formik.errors.password && formik.touched.password ? (
                            <div className="text-red-500 text-sm">
                              {formik.errors.password}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </motion.form>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="mt-4 py-2 lg:mt-8 flex items-center gap-4 justify-center"
                  >
                    <svg
                      className="w-[81px] h-[2px] lg:w-[156px]"
                      viewBox="0 0 156 2"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line y1="1" x2="155.5" y2="1" stroke="#35383F" />
                    </svg>
                    <p className="or text-[14px] lg:text-base">or</p>
                    <svg
                      className="w-[81px] h-[2px] lg:w-[156px]"
                      viewBox="0 0 156 2"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line y1="1" x2="155.5" y2="1" stroke="#35383F" />
                    </svg>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    className="mt-6 flex flex-col items-center lg:flex-row gap-3 lg:gap-8 justify-center"
                  >
                    <div className=" h-full w-[180px] font-urbanist lg:py-[13px] max-w-[258px] px-2 py-[5px] lg:w-full lg:px-[20px] rounded-[8px] lg:rounded-[16px] cursor-pointer border border-[#35383F] ">
                      <div
                        onClick={loginWithGoogle}
                        className="flex gap-1  lg:gap-3 text-[14px] lg:text-base justify-center items-center"
                      >
                        <p>
                          <svg
                            className="w-4 lg:w-[18px] lg:h-[18px] "
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_2323_12960)">
                              <path
                                d="M23.5078 12.2244C23.5078 11.2412 23.4295 10.5236 23.2598 9.77954H12.2295V14.2175H18.7041C18.5736 15.3204 17.8687 16.9814 16.3022 18.0975L16.2802 18.246L19.7678 20.9962L20.0095 21.0208C22.2285 18.9346 23.5078 15.8652 23.5078 12.2244Z"
                                fill="#4285F4"
                              />
                              <path
                                d="M12.2296 23.9176C15.4016 23.9176 18.0645 22.8545 20.0095 21.0209L16.3023 18.0976C15.3102 18.8018 13.9787 19.2934 12.2296 19.2934C9.1228 19.2934 6.48598 17.2074 5.54603 14.324L5.40825 14.3359L1.7818 17.1927L1.73438 17.3269C3.6663 21.2334 7.63462 23.9176 12.2296 23.9176Z"
                                fill="#34A853"
                              />
                              <path
                                d="M5.54621 14.324C5.2982 13.58 5.15466 12.7826 5.15466 11.9589C5.15466 11.135 5.2982 10.3378 5.53316 9.59366L5.52659 9.43519L1.8547 6.53247L1.73457 6.59064C0.938328 8.21174 0.481445 10.0322 0.481445 11.9589C0.481445 13.8855 0.938328 15.7059 1.73457 17.327L5.54621 14.324Z"
                                fill="#FBBC05"
                              />
                              <path
                                d="M12.2295 4.62403C14.4356 4.62403 15.9237 5.59401 16.7722 6.40461L20.0878 3.10928C18.0515 1.1826 15.4015 0 12.2295 0C7.63462 0 3.6663 2.68406 1.73438 6.59056L5.53297 9.59359C6.48597 6.7102 9.12279 4.62403 12.2295 4.62403Z"
                                fill="#EB4335"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_2323_12960">
                                <rect
                                  width="23.04"
                                  height="24"
                                  fill="white"
                                  transform="translate(0.480469)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        </p>
                        <p className="min-w-max">Continue with Google</p>
                      </div>
                    </div>
                    <div
                      onClick={() => {
                        router.push("/register");
                      }}
                      className=" flex justify-center items-center py-[5px] font-urbanist lg:py-[13px] max-w-[258px] w-[180px] p[] lg:w-full px-5 lg:px-[20px] rounded-[8px] lg:rounded-[16px] cursor-pointer border border-[#35383F] "
                    >
                      <div className="flex gap-3 text-[14px] lg:text-base justify-center items-center">
                        <p className="min-w-max">Create Account</p>
                      </div>
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    className="mt-7 flex justify-center text-center"
                  >
                    <p
                      onClick={() => setForgotModalOpen(true)}
                      className="w-fit underline cursor-pointer font-[600] text-[#F72BB5] "
                    >
                      Forgot Password?
                    </p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                    className="mt-5 lg:mt-5 pt-1 relative"
                  >
                    <div className="absolute w-[105%] left-[-1%]">
                      <svg
                        width="100%"
                        height="2"
                        viewBox="0 0 694 2"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <line y1="1" x2="694.001" y2="1" stroke="#332D41" />
                      </svg>
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                    className="mt-5 lg:mt-8 modal-footer flex flex-col lg:flex-row gap-4 lg:gap-8 items-center justify-end"
                  >
                    <button
                      onClick={() => {
                        formik.handleSubmit();
                      }}
                      className={`main_btn_bg font-urbanist lg:order-2 font-[800] text-[14px]  lg:text-base py-[6px] lg:py-5 px-[62px] w-full rounded-[10px] lg:leading-[10px] `}
                    >
                      {" "}
                      Continue
                    </button>
                  </motion.div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
      {forgotModalOpen && (
        <ForgotPasswordModal setForgotModalOpen={setForgotModalOpen} />
      )}
      {createNewUserModalOpen && (
        <CreateUserModal
          setCreateNewUserModalOpen={setCreateNewUserModalOpen}
        />
      )}
    </User>
  );
}
export async function getStaticProps() {
  try {
    const API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY;

    return {
      props: {
        API_KEY: API_KEY,
      },
      revalidate: 60,
    };
  } catch (error) {
    return {
      props: {
        API_KEY: null,
        error: error.message,
      },
    };
  }
}
